import ExcelJS from "exceljs";

export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let s = val == null ? "" : String(val);
    if (s.includes('"')) s = s.replace(/"/g, '""');
    return `"${s}"`;
  };

  // header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // data rows
  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

/**
 * Given an array of transactions (already filtered by store/date),
 * filters out those with zero discount, builds a CSV, and triggers download.
 *
 * @param {Object[]} transactions
 * @param {string}   [brandName="Angels Pizza"]
 * @param {string}   [fileName="Transactions_Discount_Value_Time_of_Day.csv"]
 */
// export function exportDiscountTransactionsCSV(
//   transactions,
//   brandName = "Angels Pizza",
//   fileName = "Transactions_Discount_Value_Time_of_Day.csv"
// ) {
//   if (!Array.isArray(transactions)) {
//     console.error(
//       "exportDiscountTransactionsCSV: need an array of transactions"
//     );
//     return;
//   }

//   // 1) exclude any tx with zero discount
//   const withDiscount = transactions.filter(
//     (tx) => Number(tx.discount_amount || 0) > 0
//   );

//   // 2) map into flat objects matching CSV columns
//   const rows = withDiscount.map((tx) => {
//     const date = new Date(tx.tran_date).toISOString().split("T")[0];
//     const store = tx.store_code.split("_")[1] || tx.store_code;
//     return {
//       Brand: brandName,
//       Store: store,
//       Date: date,
//       "Discount Name": tx.discount_name || "",
//       "Discount Amount": Number(tx.discount_amount || 0).toFixed(2),
//     };
//   });

//   // 3) convert to CSV string
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportDiscountTransactionsCSV: nothing to export");
//     return;
//   }

//   // 4) trigger download
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportDiscountTransactionsCSV(
  transactions,
  brandName = "Angels Pizza",
  fileName = "Transactions_Discount_Value_Time_of_Day.xlsx",
) {
  if (!Array.isArray(transactions)) {
    console.error(
      "exportDiscountTransactionsXLSX: need an array of transactions",
    );
    return;
  }

  // 1) keep only discounted tx
  const withDiscount = transactions.filter(
    (tx) => Number(tx?.discount_amount || 0) > 0,
  );

  if (!withDiscount.length) {
    console.warn("exportDiscountTransactionsXLSX: nothing to export");
    return;
  }

  // 2) rows
  const rows = withDiscount.map((tx) => {
    const d = new Date(tx.tran_date);
    const date = isNaN(d) ? "" : d.toISOString().split("T")[0];
    const hour = isNaN(d) ? "" : d.toTimeString().slice(0, 5); // HH:mm
    const store =
      typeof tx.store_code === "string"
        ? tx.store_code.includes("_")
          ? tx.store_code.split("_").slice(1).join("_")
          : tx.store_code
        : "";

    const discountAmt = Number(tx.discount_amount || 0);

    return {
      Brand: brandName,
      Store: store,
      Date: date,
      Time: hour,
      DiscountName: tx.discount_name || "",
      DiscountAmount: discountAmt,
    };
  });

  // Totals
  const totalDiscount = rows.reduce(
    (sum, r) => sum + (r.DiscountAmount || 0),
    0,
  );

  // ===== Workbook =====
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Discount Transactions", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title =====
  sheet.mergeCells("A1:F1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Discount Transactions (Value vs Time of Day)";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta =====
  sheet.mergeCells("A2:F2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: ${brandName}   |   Records: ${rows.length.toLocaleString()}   |   Total Discount: ₱${totalDiscount.toLocaleString(
    undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  )}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Header =====
  const headers = [
    "Brand",
    "Store",
    "Date",
    "Time",
    "Discount Name",
    "Discount Amount",
  ];
  sheet.addRow(headers);

  // ===== Data =====
  rows.forEach((r) => {
    sheet.addRow([
      r.Brand,
      r.Store,
      r.Date,
      r.Time,
      r.DiscountName,
      r.DiscountAmount,
    ]);
  });

  // Column widths
  sheet.columns = [
    { width: 16 }, // Brand
    { width: 18 }, // Store
    { width: 14 }, // Date
    { width: 10 }, // Time
    { width: 26 }, // Discount Name
    { width: 18 }, // Amount
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Style body
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, col) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // alignment
      if (col <= 5) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "right" };
        cell.numFmt = '"₱"#,##0.00';
      }

      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // ===== Total row =====
  const totalRowIndex = lastDataRow + 1;
  sheet.mergeCells(`A${totalRowIndex}:E${totalRowIndex}`);
  const totalLabelCell = sheet.getCell(`A${totalRowIndex}`);
  totalLabelCell.value = "TOTAL DISCOUNT";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  const totalCell = sheet.getCell(`F${totalRowIndex}`);
  totalCell.value = { formula: `SUM(F${firstDataRow}:F${lastDataRow})` };
  totalCell.font = { bold: true };
  totalCell.alignment = { vertical: "middle", horizontal: "right" };
  totalCell.numFmt = '"₱"#,##0.00';
  totalCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // Auto-filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download (no file-saver) =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
